import { useCallback, useEffect, useState } from 'react';
import {
  useCompleteS3UploadMutation,
  useGetPresignedUploadUrlMutation,
  useGetStorageQuery,
  useGetStorageStatsQuery,
  useUploadLargeFileMutation,
  useUploadStorageFilesMutation,
} from '~/api/v2/hooks';
import { ProgressStatus } from '~/pages/DatasetDetailsPage/types';
import { useAppDispatch } from '~/redux';
import { BannerType, closeBanner, showBanner } from '~/redux/slices/bannerSlice';
import { formatBytes } from '~/utils';
import usePreventPageReload from '../usePreventPageReload';

export interface UploadingFile {
  progress: number;
  status: ProgressStatus;
}

const DEFAULT_CHUNK_SIZE = 10;
const LARGE_FILE_THRESHOLD = 0.9 * 1024 * 1024;
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB limit

const useStorageFileUploader = (chunkSize: number = DEFAULT_CHUNK_SIZE) => {
  const dispatch = useAppDispatch();
  const { refetch: refetchStorage } = useGetStorageQuery({});
  const { refetch: refetchStats } = useGetStorageStatsQuery({});
  const [uploadFiles, { isLoading: isFilesUploading, isError: isFileUploadError }] = useUploadStorageFilesMutation({
    fixedCacheKey: 'storage-upload-files',
  });
  const [uploadLargeFile, { isLoading: isLargeFileUploading }] = useUploadLargeFileMutation();
  const [getPresignedUrl, { isError: isPresignedUrlError }] = useGetPresignedUploadUrlMutation();
  const [completeUpload, { isError: isCompleteUploadError }] = useCompleteS3UploadMutation();
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());
  const [chunksConfig, setChunksConfig] = useState({
    total: 0,
    current: 0,
  });
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  usePreventPageReload(isFilesUploading);
  usePreventPageReload(isLargeFileUploading);

  useEffect(() => {
    if (isFileUploadError || isPresignedUrlError || isCompleteUploadError) {
      dispatch(closeBanner());
      setUploadingFiles(new Map());
    }
  }, [isFileUploadError, isPresignedUrlError, isCompleteUploadError, dispatch]);

  useEffect(() => {
    if (isUploadComplete) {
      if (!isPresignedUrlError && !isFileUploadError && !isCompleteUploadError) {
        dispatch(
          showBanner({
            message: `Uploaded: ${chunksConfig.total} of ${chunksConfig.total} files completed`,
            type: BannerType.FILE_UPLOADED,
          }),
        );
      }
      setUploadingFiles(new Map());
      setIsUploadComplete(false);
    }
  }, [isUploadComplete, chunksConfig, dispatch]);

  useEffect(() => {
    const inProgressFilesLength = [...uploadingFiles.values()].filter(
      ({ status }) => status === ProgressStatus.RUNNING,
    ).length;
    if (
      !isPresignedUrlError &&
      !isFileUploadError &&
      !isUploadComplete &&
      (inProgressFilesLength || isFilesUploading)
    ) {
      dispatch(
        showBanner({
          message: `Uploading: ${chunksConfig.current} of ${chunksConfig.total} files complete. Do not refresh or exit your browser window until after upload complete.`,
          type: BannerType.FILE_UPLOAD,
        }),
      );
    }
  }, [
    uploadingFiles,
    isFilesUploading,
    isFileUploadError,
    isPresignedUrlError,
    isUploadComplete,
    chunksConfig,
    dispatch,
  ]);

  const handleUploadedFiles = useCallback(
    async (files: File[] | null, folderPath: string) => {
      if (!files || files.length === 0) return;

      const smallFiles = files.filter((file) => file.size <= LARGE_FILE_THRESHOLD);
      const largeFiles = files.filter((file) => file.size > LARGE_FILE_THRESHOLD);
      const totalFiles = files.length;

      const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map((f) => f.name).join(', ');
        dispatch(
          showBanner({
            message: `The following file(s) exceed the ${formatBytes(MAX_FILE_SIZE)} size limit: ${fileNames}`,
            type: BannerType.FILE_ERROR,
          }),
        );

        return;
      }

      try {
        let processedFiles = 0;

        // Handle small files in chunks via direct upload
        if (smallFiles.length > 0) {
          const chunks: File[][] = [];
          for (let i = 0; i < smallFiles.length; i += chunkSize) {
            chunks.push(smallFiles.slice(i, i + chunkSize));
          }

          for (const fileChunk of chunks) {
            setChunksConfig({
              current: processedFiles,
              total: totalFiles,
            });

            const uploadingFilesMap = fileChunk.reduce<Map<string, UploadingFile>>((acc, file) => {
              const fileId = `${file.name}`;
              acc.set(fileId, {
                progress: 0,
                status: ProgressStatus.RUNNING,
              });

              return acc;
            }, new Map());

            setUploadingFiles(uploadingFilesMap);

            await uploadFiles({
              files: fileChunk,
              onProgress: (progress: number) => {
                setUploadingFiles((prevFiles) => {
                  const newFilesMap = new Map(prevFiles);
                  const status = progress < 100 ? ProgressStatus.RUNNING : ProgressStatus.COMPLETED;

                  for (const file of fileChunk) {
                    const fileId = `${file.name}`;
                    const progressData = newFilesMap.get(fileId);
                    if (progressData) {
                      progressData.progress = progress;
                      progressData.status = status;
                    }
                  }

                  return newFilesMap;
                });
              },
              folder_path: folderPath,
            });

            processedFiles += fileChunk.length;
          }
        }

        // Handle large files via presigned URL upload
        for (const file of largeFiles) {
          const fileId = `${file.name}`;

          setChunksConfig({
            current: processedFiles,
            total: totalFiles,
          });

          setUploadingFiles((prev) => {
            const newMap = new Map(prev);
            newMap.set(fileId, {
              progress: 0,
              status: ProgressStatus.RUNNING,
            });

            return newMap;
          });

          try {
            const presignedData = await getPresignedUrl({
              file_name: file.name,
              folder_path: folderPath,
              content_type: file.type || 'application/octet-stream',
            }).unwrap();

            await uploadLargeFile({
              file,
              folder_path: folderPath,
              presignedUrl: presignedData.data.url,
              onProgress: (progress: number) => {
                setUploadingFiles((prevFiles) => {
                  const newFilesMap = new Map(prevFiles);
                  const progressData = newFilesMap.get(fileId);
                  if (progressData) {
                    progressData.progress = progress;
                    progressData.status = progress < 100 ? ProgressStatus.RUNNING : ProgressStatus.COMPLETED;
                  }

                  return newFilesMap;
                });
              },
            }).unwrap();

            await completeUpload({
              folder_path: folderPath,
              file_name: file.name,
            }).unwrap();
          } catch (error) {
            console.error(`Error uploading large file ${file.name}:`, error);
            setUploadingFiles((prevFiles) => {
              const newFilesMap = new Map(prevFiles);
              const progressData = newFilesMap.get(fileId);
              if (progressData) {
                progressData.status = ProgressStatus.COMPLETED;
              }

              return newFilesMap;
            });
          }

          processedFiles += 1;
        }

        // Refetch storage data after all files are uploaded
        await Promise.all([refetchStats(), refetchStorage()]);

        // Mark upload as complete to trigger success banner
        setIsUploadComplete(true);
      } catch (error) {
        console.error('Error uploading files:', error);
        throw error;
      }
    },
    [uploadFiles, uploadLargeFile, getPresignedUrl, completeUpload, chunkSize, refetchStats, refetchStorage, dispatch],
  );

  return {
    handleUploadedFiles,
    uploadingFiles,
    isFilesUploading,
  };
};

export default useStorageFileUploader;
